import { useState, useMemo, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useWorkItemStore } from '../../store/useWorkItemStore';
import { useConfigStore } from '../../store/useConfigStore';
import { useResourceStore } from '../../store/useResourceStore';
import type { WorkItemType, WorkItem } from '../../types';

interface Props {
  projectId: string;
  parentId?: string | null;
  defaultType?: WorkItemType;
  onClose: () => void;
}

/**
 * Hierarchy rules enforced here:
 *   epic    → no parent (top-level under project)
 *   feature → parent must be an epic
 *   story   → parent must be a feature
 *   task    → parent must be any work item
 */

const ALLOWED_PARENT_TYPES: Record<WorkItemType, WorkItemType[]> = {
  epic: [],
  feature: ['epic'],
  story: ['feature'],
  task: ['epic', 'feature', 'story', 'task'],
  bug: ['epic', 'feature', 'story'],
  security: ['epic', 'feature', 'story'],
  design: ['epic', 'feature', 'story'],
  idea: [], // ideas are proposed via the Ideation wizard, not this modal
  lead: [], // leads are contact records; they arrive via intake, not here
  opportunity: [], // commercial, top-level, and may exist with no lead
  // Raised by the dispatcher when a delivery dead-letters. Never created here:
  // a hand-made one would have no outbox entry to replay.
  integration: [],
};

// What child types are valid for a given parent type
const CHILD_TYPES_FOR_PARENT: Record<WorkItemType, WorkItemType[]> = {
  epic: ['feature', 'task', 'bug', 'security', 'design'],
  feature: ['story', 'task', 'bug', 'security', 'design'],
  story: ['task', 'bug', 'security', 'design'],
  task: ['task'],
  bug: ['task'],
  security: ['task'],
  design: [],
  idea: [],
  lead: [],
  opportunity: [],
  // Auto-created by the dispatcher on dead-letter, so it has no natural
  // parent and must never be given one by hand.
  integration: [],
};

export default function CreateWorkItemModal({ projectId, parentId, defaultType, onClose }: Props) {
  const { items, createItem } = useWorkItemStore();
  const { config } = useConfigStore();
  const resources = useResourceStore((s) => s.resources);
  const activeResources = useMemo(() => resources.filter((r) => r.active), [resources]);

  // Resolve parent item
  const parentItem = parentId ? items.find((i) => i._id === parentId) : null;

  // Determine allowed work item types based on context
  const allowedTypes = useMemo((): WorkItemType[] => {
    if (defaultType) return [defaultType];
    if (parentItem) {
      return CHILD_TYPES_FOR_PARENT[parentItem.workItemType] || ['task'];
    }
    // Top-level: allow all types; the form will guide parent selection
    return ['epic', 'feature', 'story', 'task', 'bug', 'security'];
  }, [parentItem, defaultType]);

  const getDefaultType = (): WorkItemType => {
    if (defaultType) return defaultType;
    if (parentItem) {
      const children = CHILD_TYPES_FOR_PARENT[parentItem.workItemType];
      return children[0] || 'task';
    }
    return 'epic';
  };

  const [form, setForm] = useState({
    title: '',
    description: '',
    workItemType: getDefaultType(),
    status: 'backlog',
    priority: 'medium',
    assignee: '',
    startDate: '',
    dueDate: '',
    estimatedHours: '',
    storyPoints: '',
    tags: '',
    parentId: parentId || '',
  });
  // Security-specific customFields (only used when workItemType === 'security')
  const [securityFields, setSecurityFields] = useState({
    severity: 'medium',
    remediationStatus: 'triage',
    disclosureStage: 'internal',
    owaspCategory: '',
    discoveryMethod: '',
  });
  const updateSec = (field: string, value: string) =>
    setSecurityFields((prev) => ({ ...prev, [field]: value }));
  const [loading, setLoading] = useState(false);
  const [showInlineEpic, setShowInlineEpic] = useState(false);
  const [inlineEpicTitle, setInlineEpicTitle] = useState('');

  // Potential parents for the currently selected workItemType
  const potentialParents = useMemo((): WorkItem[] => {
    const wit = form.workItemType as WorkItemType;
    const parentTypes = ALLOWED_PARENT_TYPES[wit];
    if (parentTypes.length === 0) return []; // epics have no parent
    return items.filter((i) => parentTypes.includes(i.workItemType as WorkItemType));
  }, [form.workItemType, items]);

  // Whether this type requires a parent
  const requiresParent = form.workItemType !== 'epic';

  // When type changes, reset parent selection
  const handleTypeChange = (newType: string) => {
    const wt = newType as WorkItemType;
    // If parent was already set and is valid, keep it
    if (form.parentId) {
      const parentTypes = ALLOWED_PARENT_TYPES[wt];
      const parent = items.find((i) => i._id === form.parentId);
      if (parent && parentTypes.includes(parent.workItemType as WorkItemType)) {
        setForm((prev) => ({ ...prev, workItemType: wt }));
        return;
      }
    }
    setForm((prev) => ({ ...prev, workItemType: wt, parentId: '' }));
  };

  const handleCreateInlineEpic = async () => {
    if (!inlineEpicTitle.trim()) return;
    try {
      const epic = await createItem({
        projectId,
        parentId: null,
        title: inlineEpicTitle.trim(),
        workItemType: 'epic',
        status: 'backlog',
        priority: 'medium',
      } as any);
      setForm((prev) => ({ ...prev, parentId: epic._id }));
      setInlineEpicTitle('');
      setShowInlineEpic(false);
      toast.success('Epic created');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create epic');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (requiresParent && !form.parentId) {
      toast.error(`A ${form.workItemType} requires a parent work item`);
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        projectId,
        parentId: form.parentId || null,
        title: form.title,
        description: form.description,
        workItemType: form.workItemType as WorkItemType,
        status: form.status,
        priority: form.priority as any,
        assignee: form.assignee,
        startDate: form.startDate,
        dueDate: form.dueDate,
        estimatedHours: form.estimatedHours ? parseFloat(form.estimatedHours) : 0,
        storyPoints: form.storyPoints ? Math.round(parseFloat(form.storyPoints) * 100) / 100 : 0,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()) : [],
      };
      if (form.workItemType === 'security') {
        const cf: Record<string, any> = {
          severity: securityFields.severity,
          remediationStatus: securityFields.remediationStatus,
          disclosureStage: securityFields.disclosureStage,
        };
        if (securityFields.owaspCategory) cf.owaspCategory = securityFields.owaspCategory;
        if (securityFields.discoveryMethod) cf.discoveryMethod = securityFields.discoveryMethod;
        payload.customFields = cf;
      }
      await createItem(payload);
      toast.success(`${form.workItemType} created`);
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create item');
    } finally {
      setLoading(false);
    }
  };

  const update = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // Helper label
  const parentLabel = (): string => {
    const wt = form.workItemType as WorkItemType;
    const types = ALLOWED_PARENT_TYPES[wt];
    if (types.length === 0) return '';
    return `Parent ${types.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(' / ')} *`;
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Create Work Item
                  </Dialog.Title>
                  <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                    <X size={18} className="text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Row 1: Type + Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type *</label>
                      <select
                        value={form.workItemType}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        {config.workItemTypes
                          .filter((t) => allowedTypes.includes(t.id as WorkItemType))
                          .map((t) => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <select
                        value={form.status}
                        onChange={(e) => update('status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        {config.statuses.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Parent selector (only for feature / story / task) */}
                  {requiresParent && !parentId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {parentLabel()}
                      </label>

                      {potentialParents.length > 0 ? (
                        <select
                          value={form.parentId}
                          onChange={(e) => update('parentId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                        >
                          <option value="">— Select parent —</option>
                          {potentialParents.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.title} ({p.workItemType})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                          No {ALLOWED_PARENT_TYPES[form.workItemType as WorkItemType].join('/')}s exist yet.
                          {form.workItemType === 'feature' && (
                            <button
                              type="button"
                              onClick={() => setShowInlineEpic(true)}
                              className="ml-2 underline font-medium hover:text-amber-700 dark:hover:text-amber-300"
                            >
                              Create an Epic first
                            </button>
                          )}
                          {form.workItemType === 'story' && (
                            <span className="ml-1">Create a Feature (under an Epic) first.</span>
                          )}
                        </div>
                      )}

                      {/* Inline epic creation for features */}
                      {showInlineEpic && form.workItemType === 'feature' && (
                        <div className="mt-2 flex gap-2">
                          <input
                            type="text"
                            value={inlineEpicTitle}
                            onChange={(e) => setInlineEpicTitle(e.target.value)}
                            placeholder="New epic title..."
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={handleCreateInlineEpic}
                            disabled={!inlineEpicTitle.trim()}
                            className="flex items-center gap-1 px-3 py-2 bg-accent text-white rounded-lg text-sm hover:bg-accent/90 disabled:opacity-50"
                          >
                            <Plus size={14} />
                            Create
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show locked parent when parentId was provided externally */}
                  {parentId && parentItem && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parent</label>
                      <p className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        {parentItem.title}{' '}
                        <span className="text-xs text-gray-400">({parentItem.workItemType})</span>
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => update('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      placeholder="Work item title..."
                      autoFocus={!showInlineEpic}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => update('description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                      <select
                        value={form.priority}
                        onChange={(e) => update('priority', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        {config.priorities.map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assignee</label>
                      <select
                        value={form.assignee}
                        onChange={(e) => update('assignee', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        <option value="">— Unassigned —</option>
                        {activeResources.filter((r) => r.resourceType === 'user' || !r.resourceType).length > 0 && (
                          <optgroup label="Users">
                            {activeResources.filter((r) => r.resourceType === 'user').map((r) => (
                              <option key={r._id} value={r.name}>{r.name}</option>
                            ))}
                          </optgroup>
                        )}
                        {activeResources.filter((r) => r.resourceType === 'token').length > 0 && (
                          <optgroup label="Tokens">
                            {activeResources.filter((r) => r.resourceType === 'token').map((r) => (
                              <option key={r._id} value={r.name}>{r.name}</option>
                            ))}
                          </optgroup>
                        )}
                        {activeResources.filter((r) => r.resourceType === 'resource' || (!r.resourceType && r.resourceType !== 'user')).length > 0 && (
                          <optgroup label="Resources">
                            {activeResources.filter((r) => r.resourceType === 'resource' || !r.resourceType).map((r) => (
                              <option key={r._id} value={r.name}>{r.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={form.startDate}
                        onChange={(e) => update('startDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={form.dueDate}
                        onChange={(e) => update('dueDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Story Points</label>
                      {/* Any value 0-100 to two decimals (1 SP = 1 h per sprint working day);
                          the list only offers quick picks. */}
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.05"
                        list="story-point-picks"
                        value={form.storyPoints}
                        onChange={(e) => update('storyPoints', e.target.value)}
                        placeholder="e.g. 0.8"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      />
                      <datalist id="story-point-picks">
                        {[0.25, 0.5, 1, 2, 3, 5, 8, 13].map((v) => (
                          <option key={v} value={v} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estimated Hours</label>
                      <input
                        type="number"
                        value={form.estimatedHours}
                        onChange={(e) => update('estimatedHours', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                        min="0"
                        step="0.5"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tags <span className="text-gray-400 dark:text-gray-500 font-normal">(comma-separated)</span>
                    </label>
                    <input
                      type="text"
                      value={form.tags}
                      onChange={(e) => update('tags', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                    />
                  </div>

                  {/* Security-specific fields */}
                  {form.workItemType === 'security' && (
                    <div className="p-3 bg-orange-50 dark:bg-orange-900/10 rounded-lg border border-orange-200 dark:border-orange-800/50 space-y-3">
                      <h4 className="text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wide">Security Triage</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Severity *</label>
                          <select
                            value={securityFields.severity}
                            onChange={(e) => updateSec('severity', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                          >
                            {['info','low','medium','high','critical'].map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Remediation</label>
                          <select
                            value={securityFields.remediationStatus}
                            onChange={(e) => updateSec('remediationStatus', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                          >
                            {['triage','confirmed','in_remediation','mitigated','fixed','wont_fix','duplicate'].map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Disclosure</label>
                          <select
                            value={securityFields.disclosureStage}
                            onChange={(e) => updateSec('disclosureStage', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                          >
                            {['internal','coordinated','public','embargoed'].map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">OWASP Category</label>
                          <input
                            type="text"
                            placeholder="e.g. A02:2021 - Cryptographic Failures"
                            value={securityFields.owaspCategory}
                            onChange={(e) => updateSec('owaspCategory', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Discovery</label>
                          <select
                            value={securityFields.discoveryMethod}
                            onChange={(e) => updateSec('discoveryMethod', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                          >
                            <option value="">—</option>
                            {['manual_review','sast','dast','sca','pentest','bug_bounty','external_disclosure','incident'].map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || (requiresParent && !form.parentId)}
                      className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50"
                    >
                      {loading ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
